import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { MaskLine } from "../components/ui-bits";
import { Logo } from "../components/Logo";
import ApplyChat from "../components/ApplyChat";
import { useLang } from "../i18n/LangContext";

const COPY = {
  en: {
    back: "Back",
    h1: "Get indicative terms.",
    steps: ["Chat with Verde about your project", "Submit your case for review", "Receive indicative terms by email — within 24 hours"],
    doneH: "Project received.",
    donePre: "Thank you, ",
    doneName: (n) => n.split(" ")[0],
    doneNoName: "there",
    doneMid: ". Verde Capital's credit team will review your case and follow up personally. No automatic approval — a human looks at every submission.",
    doneRef: "Your reference",
    doneHome: "Back to home",
  },
  pt: {
    back: "Voltar",
    h1: "Obtenha condições indicativas.",
    steps: ["Converse com a Verde sobre o seu projeto", "Submeta o processo para análise", "Receba condições indicativas por email — em 24 horas"],
    doneH: "Projeto recebido.",
    donePre: "Obrigado",
    doneName: (n) => n.split(" ")[0],
    doneNoName: "",
    doneMid: ". A equipa de crédito da Verde Capital irá analisar o seu processo e entrar em contacto pessoalmente. Sem aprovação automática — uma pessoa analisa cada apresentação.",
    doneRef: "A sua referência",
    doneHome: "Voltar ao início",
  },
  nl: {
    back: "Terug",
    h1: "Vraag indicatieve voorwaarden aan.",
    steps: ["Praat met Verde over uw project", "Dien het dossier ter beoordeling in", "Ontvang indicatieve voorwaarden per e-mail — binnen 24 uur"],
    doneH: "Project ontvangen.",
    donePre: "Bedankt",
    doneName: (n) => n.split(" ")[0],
    doneNoName: "",
    doneMid: ". Het kredietteam van Verde Capital beoordeelt uw dossier en neemt persoonlijk contact op. Geen automatische goedkeuring — een mens beoordeelt elke inzending.",
    doneRef: "Uw referentie",
    doneHome: "Terug naar de homepage",
  },
};

export default function Apply() {
  const { lang, lp } = useLang();
  const t = COPY[lang];
  const prefill = useLocation().state?.prefill || null;
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(null); // { reference, name }

  if (submitted) {
    const first = submitted.name ? t.doneName(submitted.name) : t.doneNoName;
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-xl w-full text-center"
          data-testid="apply-confirmation"
        >
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-verde-800">
            <Check className="h-6 w-6 text-mint" />
          </span>
          <h1 className="display mt-8 text-4xl sm:text-5xl text-charcoal">{t.doneH}</h1>
          <p className="mt-5 text-ink leading-relaxed">
            {t.donePre}{first ? `${first},` : ""}{t.doneMid}
          </p>
          <div className="mt-8 inline-block rounded-2xl border border-sand bg-white px-8 py-5">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-fog">{t.doneRef}</p>
            <p className="mt-2 font-mono text-xl tracking-[0.14em] text-verde-800" data-testid="apply-reference">{submitted.reference}</p>
          </div>
          <div className="mt-10">
            <Link to={lp("/")} className="text-sm font-semibold text-verde-800 underline underline-offset-4" data-testid="confirmation-home-link">
              {t.doneHome}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <section className="pt-7 sm:pt-9 pb-9 sm:pb-14">
        <div className="container-x flex items-center justify-between" data-testid="apply-topbar">
          <Link to={lp("/")} data-testid="apply-logo" aria-label="Verde Capital home">
            <Logo />
          </Link>
          <button
            type="button"
            onClick={() => {
              if (window.history.state && window.history.state.idx > 0) navigate(-1);
              else navigate(lp("/"));
            }}
            data-testid="apply-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-sand bg-white px-4 py-2 text-sm font-semibold text-verde-800 shadow-sm transition-colors duration-300 hover:border-verde-700 sm:px-5 sm:py-2.5"
          >
            <ArrowLeft className="h-4 w-4" />
            {t.back}
          </button>
        </div>
        <div className="container-x max-w-3xl mt-12 sm:mt-16 text-center">
          <h1 className="display text-[2.4rem] sm:text-5xl text-charcoal">
            <MaskLine>{t.h1}</MaskLine>
          </h1>
          <ol className="mt-8 flex flex-col items-start justify-center gap-4 sm:flex-row sm:items-center sm:gap-0" data-testid="apply-steps-guide">
            {t.steps.map((s, i) => (
              <li key={s} className="flex items-center sm:items-center sm:gap-0">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verde-800 font-display text-sm font-bold text-white shadow-md shadow-verde-950/15">
                  {i + 1}
                </span>
                <span className="ml-3 max-w-[210px] text-left text-[13px] font-medium leading-snug text-ink">
                  {s}
                </span>
                {i < t.steps.length - 1 && <span aria-hidden="true" className="mx-4 hidden h-px w-8 shrink-0 bg-sand sm:block" />}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <ApplyChat
            prefill={prefill}
            onSubmitted={(reference, name) => {
              setSubmitted({ reference, name });
              window.scrollTo(0, 0);
            }}
          />
        </div>
      </section>
    </div>
  );
}
